import { useEffect, useState } from "react";
import axios from "axios";

import { GET_URL } from "../utils";
import { GetResultsResponse } from "../types";

const LATEST_YEAR_CACHE_KEY = "latest";

/** Kept for the page lifetime, so switching back to an already loaded year does not refetch it. */
const responseCache = new Map<string, GetResultsResponse>();
/** Requests in flight, so a prefetch and a selection of the same year share one request. */
const pendingRequests = new Map<string, Promise<GetResultsResponse>>();

const getCacheKey = (year: number | undefined) => year === undefined ? LATEST_YEAR_CACHE_KEY : `${year}`;

/** Accepts also the response of the previous backend version (without years), so the deploy order does not matter. */
const normalizeResponse = (data: unknown, requestedYear: number | undefined): GetResultsResponse | undefined => {
    if (typeof data !== "object" || data === null || !("data" in data)) {
        return undefined;
    }
    const response = data as Partial<GetResultsResponse>;
    return {
        status: response.status ?? 200,
        year: response.year ?? requestedYear ?? (new Date()).getFullYear(),
        years: Array.isArray(response.years) ? response.years : [],
        data: response.data ?? null,
    };
};

const fetchResults = (year: number | undefined): Promise<GetResultsResponse> => {
    const cacheKey = getCacheKey(year);
    const cachedResponse = responseCache.get(cacheKey);
    if (cachedResponse !== undefined) {
        return Promise.resolve(cachedResponse);
    }
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest !== undefined) {
        return pendingRequest;
    }

    // A simple GET without custom headers, so the browser skips the CORS preflight.
    const request = axios({
        method: "get",
        url: GET_URL,
        params: year === undefined ? undefined : { year },
    }).then(({ data }) => {
        const response = normalizeResponse(data, year);
        if (response === undefined) {
            throw new TypeError("Unexpected response of the get function.");
        }
        responseCache.set(cacheKey, response);
        responseCache.set(`${response.year}`, response);
        return response;
    }).finally(() => pendingRequests.delete(cacheKey));
    pendingRequests.set(cacheKey, request);
    return request;
};

/** Warms the cache in the background. Failures are ignored, a real load of the year retries. */
export const prefetchResults = (years: number[]) => years.forEach((year) => {
    fetchResults(year).catch(() => undefined);
});

export type ResultsState = {
    response?: GetResultsResponse,
    isLoading: boolean,
    isError: boolean,
};

/** Loads results of the given year, or of the latest year with results when no year is given. */
export const useResults = (year: number | undefined): ResultsState => {
    const cacheKey = getCacheKey(year);
    const [state, setState] = useState<ResultsState>({ isLoading: true, isError: false });

    useEffect(() => {
        const cachedResponse = responseCache.get(cacheKey);
        if (cachedResponse !== undefined) {
            setState({ response: cachedResponse, isLoading: false, isError: false });
            return;
        }

        // Drops a stale response when the year is switched before it arrives.
        let isCurrent = true;
        setState((previous) => ({ ...previous, isLoading: true, isError: false }));
        fetchResults(year).then((response) => {
            if (isCurrent) {
                setState({ response, isLoading: false, isError: false });
            }
        }).catch(() => {
            if (isCurrent) {
                setState((previous) => ({ ...previous, isLoading: false, isError: true }));
            }
        });

        return () => {
            isCurrent = false;
        };
    }, [cacheKey, year]);

    return state;
};
